import * as React from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

/**
 * MultiSelectFilter - A filter component that allows selecting multiple options
 * Supports both simple strings and objects with {value, label} format
 * The dropdown stays open after each selection for easy multi-selection
 */
export function MultiSelectFilter({
  values = [],
  onValuesChange,
  options = [],
  placeholder = "Sélectionner...",
  emptyText = "Aucun résultat.",
  allLabel = "Tous",
  className,
  "data-testid": dataTestId,
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  // Normalize options to always have {value, label} format
  const normalizedOptions = React.useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    return options
      .filter(opt => opt !== null && opt !== undefined)  // Filter out null/undefined
      .map(opt => {
        if (typeof opt === 'string') {
          return { value: opt, label: opt };
        }
        if (opt && typeof opt === 'object') {
          return { value: opt.value || '', label: opt.label || opt.value || '' };
        }
        return { value: String(opt), label: String(opt) };
      })
      .filter(opt => opt.value);  // Filter out empty values
  }, [options]);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return normalizedOptions;
    const search = searchValue.toLowerCase();
    return normalizedOptions.filter((option) =>
      (option.label || '').toLowerCase().includes(search) ||
      (option.value || '').toLowerCase().includes(search)
    );
  }, [normalizedOptions, searchValue]);

  // Get label for a value
  const getLabelForValue = (value) => {
    if (!value) return '';
    const opt = normalizedOptions.find(o => o.value === value);
    return opt ? opt.label : value;
  };

  const handleToggle = (optionValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (values.includes(optionValue)) {
      onValuesChange(values.filter((v) => v !== optionValue));
    } else {
      onValuesChange([...values, optionValue]);
    }
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onValuesChange([]);
  };

  const handleRemove = (optionValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    onValuesChange(values.filter((v) => v !== optionValue));
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onValuesChange([]);
  };

  const isAllSelected = values.length === 0;

  // Truncate label if too long
  const truncateLabel = (label, maxLen = 20) => {
    if (label.length <= maxLen) return label;
    return label.substring(0, maxLen) + '...';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={dataTestId}
          className={cn(
            "w-full justify-between font-normal overflow-hidden h-auto min-h-[40px]",
            isAllSelected && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <div className="flex flex-wrap gap-1 flex-1 text-left">
            {values.length === 0 ? (
              <span>{allLabel}</span>
            ) : values.length <= 2 ? (
              values.map((value) => (
                <Badge
                  key={value}
                  variant="secondary"
                  className="text-xs font-normal px-1.5 py-0.5 max-w-[150px] truncate"
                  title={getLabelForValue(value)}
                >
                  {truncateLabel(getLabelForValue(value).split(' (')[0])}
                  <span
                    role="button"
                    tabIndex={0}
                    className="ml-1 hover:text-destructive cursor-pointer"
                    onClick={(e) => handleRemove(value, e)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRemove(value, e)}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            ) : (
              <Badge variant="secondary" className="text-xs font-normal">
                {values.length} sélectionnés
                <span
                  role="button"
                  tabIndex={0}
                  className="ml-1 hover:text-destructive cursor-pointer"
                  onClick={handleClearAll}
                  onKeyDown={(e) => e.key === 'Enter' && handleClearAll(e)}
                >
                  <X className="h-3 w-3" />
                </span>
              </Badge>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[320px] min-w-[280px] p-0" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col">
          {/* Search input */}
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-1">
              {/* "All" option */}
              <div
                role="button"
                tabIndex={0}
                onClick={handleSelectAll}
                onKeyDown={(e) => e.key === 'Enter' && handleSelectAll(e)}
                className={cn(
                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                  isAllSelected && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    isAllSelected ? "opacity-100" : "opacity-0"
                  )}
                />
                {allLabel}
              </div>

              {/* Separator */}
              {filteredOptions.length > 0 && (
                <div className="-mx-1 my-1 h-px bg-border" />
              )}

              {/* Options */}
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {emptyText}
                </div>
              ) : (
                <>
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {filteredOptions.length} client{filteredOptions.length > 1 ? 's' : ''}
                  </div>
                  {filteredOptions.map((option) => {
                    const isSelected = values.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleToggle(option.value, e)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggle(option.value, e)}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                          isSelected && "bg-accent/50"
                        )}
                        title={option.label}
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 border rounded flex items-center justify-center shrink-0",
                          isSelected ? "bg-primary border-primary" : "border-gray-300"
                        )}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span className="truncate">{option.label}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
